import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clock } from '@/lib/clock';
import { cn } from '@/lib/utils';
import { initialPlayer, loadedPercent, nextSpeed, playerReducer, SOFT_RETRY_MS, STALL_MS } from '@/lib/audioPlayerState';

/**
 * Общий аудиоплеер: запись урока, все экраны IELTS, голосовой ответ ученика,
 * запись в домашке у куратора. Один на всё приложение.
 *
 * ⚠️ СВОИ КНОПКИ ВМЕСТО `<audio controls>` (10.09.2026). Родные кнопки на
 * телефоне не показывали загрузку вовсе, у каждого браузера выглядели по-своему
 * и были мелкими для пальца. Здесь: играть/пауза, шкала с перемоткой, время,
 * скорость — зоны касания 44px; загрузка крутилкой; ошибка с «Повторить».
 * Переходы состояния — lib/audioPlayerState.ts, там же и проверка.
 *
 * ⚠️ `play()` ЗОВЁТСЯ СРАЗУ, ВНУТРИ НАЖАТИЯ, а состояние приходит из событий
 * элемента. Разрешение браузера на звук выдаётся под жест и истекает, если
 * сперва чего-то ждать (см. docs/platform/06-frontend/02-design-system.md).
 *
 * ⚠️ ЗАПИСЬ НЕ КАЧАЕТСЯ ЦЕЛИКОМ ДО ИГРЫ, как в camp: записи IELTS идут до
 * получаса, а files-service отдаёт куски (Range). Из camp взято поведение —
 * не верить событиям до конца, а держать сторож времени.
 *
 * ⚠️ ДВА ОЖИДАНИЯ, А НЕ ОДНО (12.09.2026). Через `SOFT_RETRY_MS` появляется
 * «Пока тихо» и «Повторить загрузку» — это ПРЕДЛОЖЕНИЕ, плеер продолжает
 * грузить; через `STALL_MS` приходит ошибка, как и раньше. Молчать двадцать
 * секунд и только потом дать выход — слишком долго для ученика на телефоне,
 * а называть ошибкой трёхсекундное ожидание — враньё.
 *
 * ⚠️ ПРОЦЕНТ ЗАГРУЗКИ ПОКАЗЫВАЕТСЯ НЕ ВСЕГДА, и это главное в нём. Честно
 * считать долю скачанного можно только там, где браузер тянет файл подряд с
 * начала: у длинной записи (Range), после перемотки и при неизвестной длине
 * число будет бодрым и неверным. Решает `loadedPercent` в
 * lib/audioPlayerState.ts, здесь только источник — `el.buffered`.
 *
 * ⚠️ ОГРАНИЧЕНИЙ НЕТ НАМЕРЕННО. Пауза и перемотка есть везде, в том числе в
 * Mock и Listening: так было и с родными кнопками, и в коде экранов это
 * записано как решение («пауза и перемотка нужны прямо во время ответа»).
 *
 * ⚠️ ОШИБКА НЕ ПРЯЧЕТ ПЛЕЕР. Не загрузилось или браузер не умеет этот файл
 * (webm на старом Safari) — видно, что запись есть, и рядом «Повторить» и
 * «Скачать», если ссылка на скачивание передана.
 *
 * Класс корня `ob-audio` держит плеер живым в разборе урока (`.review-inert`
 * в index.css): куратор должен слышать голосовой ответ ученика.
 */
export type AudioPlayerProps = {
  src: string;
  /** 'metadata' — по умолчанию: длительность видна, байты не качаются зря. */
  preload?: 'none' | 'metadata' | 'auto';
  /** Длительность, если известна заранее (свою запись мы засекли по часам). */
  knownSeconds?: number | null;
  /** Ссылка «скачать» — мелко, под плеером. */
  downloadUrl?: string;
  className?: string;
  /**
   * Длительность, как только проигрыватель её узнал. Блок записи судит по ней о
   * сохранённой записи короче 5 секунд (11.09.2026) — в ответе сервера её нет.
   */
  onDuration?: (seconds: number) => void;
  /**
   * Перейти к месту и включить (разбор мока, «Слушать с 2:11», 20.10.2026). Срабатывает
   * на каждый новый `nonce` — повторное нажатие той же кнопки тоже перематывает.
   */
  jumpTo?: { sec: number; nonce: number } | null;
  /**
   * Запись звучит один раз, как на экзамене (мок на компьютере, слово владельца 21.10.2026):
   * перемотки и скорости нет, после конца повтора нет. Пауза есть (правка владельца 21.10.2026,
   * после демо): Pause / Play с того же места, кнопка подписана по-английски.
   */
  examOnce?: boolean;
  /** Запуск снаружи: каждое новое значение включает запись (следующая часть мока). Дослушанную
   *  на экзамене не включает. */
  startNonce?: number;
  /** Запись заиграла (в том числе после паузы). */
  onPlaying?: () => void;
  /** Запись доиграла до конца. */
  onEnded?: () => void;
  /** Мок (техдолг 432): с какого места начать — читается при появлении плеера, сохранённое
   *  место части после F5 или возврата с вкладки Reading. */
  startAt?: number;
  /** Мок (техдолг 432): запись уже дослушана в этой попытке — как после конца, повтора нет. */
  closed?: boolean;
  /** Ход записи, секунды (timeupdate). */
  onTime?: (seconds: number) => void;
  /** Пауза — кнопкой или потому, что заиграла другая запись. Не зовётся на конце записи. */
  onPaused?: () => void;
};

// Одновременно играет одна запись: включив вторую, первую ставим на паузу.
let playingNow: HTMLAudioElement | null = null;

/**
 * Есть ли файл на сервере — отдельным запросом.
 *
 * ⚠️ И код 4 у элемента, и `NotSupportedError` от `play()` браузер отдаёт как на
 * чужой формат, так и на пропавший файл (404/503). Не проверив файл, нельзя
 * честно сказать ни «не загрузилась», ни «браузер не может» (errorKind в
 * lib/audioPlayerState.ts). Сетевой отказ самого запроса — не ответ: молчим,
 * остаётся «не загрузилась».
 */
function checkFile(url: string, onResult: (reachable: boolean) => void) {
  fetch(url, { method: 'HEAD' })
    .then((r) => onResult(r.ok))
    .catch(() => undefined);
}

/**
 * От какой длины считать процент.
 *
 * ⚠️ ЗАГОЛОВОК ФАЙЛА ВРЁТ, И ЭТО ИЗМЕРЕНО. У записей с микрофона метки времени
 * пишет браузер: на проде 2,4 секунды звука лежали в базе как 301,7 (разбор
 * `CHECK-2026-09-12-RECORD-TRUNC.md`), а у 26 записей заголовок был длиннее
 * звука больше чем на три секунды. Считать долю скачанного от такой длины
 * значит показать ученику бодрое и неверное число.
 *
 * Поэтому, когда длина известна снаружи (`knownSeconds` — это замер сервера ПО
 * ЗВУКУ, `data.recording.server_seconds`), берём её, а к `el.duration`
 * обращаемся только если своей цифры нет. У части записей `el.duration` к тому
 * же бесконечен, пока файл не дочитан, — там процента не будет вовсе, и это
 * честнее выдуманного.
 */
function percentDuration(el: HTMLAudioElement, knownSeconds?: number | null): number | null {
  if (knownSeconds != null && Number.isFinite(knownSeconds) && knownSeconds > 0) return knownSeconds;
  return Number.isFinite(el.duration) ? el.duration : null;
}

/** Куски, которые браузер уже скачал, — в вид, понятный `loadedPercent`. */
function bufferedRanges(el: HTMLAudioElement): [number, number][] {
  const out: [number, number][] = [];
  const b = el.buffered;
  for (let i = 0; i < b.length; i += 1) out.push([b.start(i), b.end(i)]);
  return out;
}

export function AudioPlayer({
  src, preload = 'metadata', knownSeconds, downloadUrl, className, onDuration, jumpTo, examOnce = false, startNonce, onPlaying, onEnded,
  startAt, closed = false, onTime, onPaused,
}: AudioPlayerProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLAudioElement | null>(null);
  // Последние onEnded, onPlaying, onTime, onPaused — в ref: обработчики элемента вешаются один раз на запись.
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onPlayingRef = useRef(onPlaying);
  onPlayingRef.current = onPlaying;
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;
  const onPausedRef = useRef(onPaused);
  onPausedRef.current = onPaused;
  const startAtRef = useRef(startAt);
  startAtRef.current = startAt;
  // Место, куда встать, как только элемент узнает длину (preload="none": только после play()).
  const pendingStart = useRef(0);
  const [state, dispatch] = useReducer(playerReducer, initialPlayer);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState<number | null>(knownSeconds ?? null);
  const [rate, setRate] = useState(1);
  const rateRef = useRef(1);
  // Признак жизни для сторожа: растёт на каждом событии загрузки и времени.
  const [signal, setSignal] = useState(0);
  // Сколько файла скачано, когда это можно сказать честно (иначе null).
  const [loaded, setLoaded] = useState<number | null>(null);
  // ⚠️ Перемотка делает процент бессмысленным: буфер уезжает от начала, и
  // «загружено 40 %» перестаёт быть долей скачанного. После неё молчим до
  // смены записи или «Повторить загрузку».
  const seeked = useRef(false);
  const fixing = useRef(false);
  // Номер текущей записи: ответ проверки файла, пришедший после смены записи
  // или ухода со страницы, выбрасываем.
  const srcToken = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let alive = true;
    srcToken.current += 1;
    dispatch({ type: 'src' });
    setCurrent(0);
    setDuration(knownSeconds ?? null);
    setLoaded(null);
    seeked.current = false;
    fixing.current = false;
    const from = startAtRef.current;
    pendingStart.current = from != null && Number.isFinite(from) && from > 0 ? from : 0;
    if (pendingStart.current > 0) {
      // Процент от начала тут бессмыслен — буфер начнётся с середины, как после перемотки.
      seeked.current = true;
      setCurrent(pendingStart.current);
    }
    // Встать на сохранённое место — один раз, когда длина известна.
    const applyStart = () => {
      if (pendingStart.current <= 0) return;
      el.currentTime = pendingStart.current;
      pendingStart.current = 0;
    };

    const life = () => setSignal((n) => n + 1);
    // Длину берём у самого элемента: у записи с микрофона она бесконечна, пока
    // не досчитана, и `loadedPercent` в этом случае честно молчит.
    const readLoaded = () =>
      setLoaded(
        loadedPercent({
          ranges: bufferedRanges(el),
          duration: percentDuration(el, knownSeconds),
          seeked: seeked.current,
        }),
      );
    // ⚠️ У записи с микрофона длины в заголовке нет — браузер отдаёт
    // бесконечность, и на шкале не видно ни длины, ни положения. Лечится
    // перемоткой в недостижимый конец: браузер дочитывает файл и выставляет
    // настоящую длину, после чего возвращаемся в начало.
    const finishFix = () => {
      if (!fixing.current || !Number.isFinite(el.duration)) return false;
      fixing.current = false;
      setDuration(el.duration);
      if (pendingStart.current > 0) applyStart();
      else el.currentTime = 0;
      return true;
    };
    const handlers: [string, () => void][] = [
      ['loadstart', () => dispatch({ type: 'loadstart' })],
      ['waiting', () => { dispatch({ type: 'waiting' }); readLoaded(); }],
      ['canplay', () => { dispatch({ type: 'canplay' }); life(); readLoaded(); }],
      ['playing', () => {
        if (playingNow && playingNow !== el) playingNow.pause();
        playingNow = el;
        dispatch({ type: 'playing' });
        life();
        onPlayingRef.current?.();
      }],
      // На конце записи браузер шлёт pause перед ended — это не пауза ученика.
      ['pause', () => { dispatch({ type: 'pause' }); if (!el.ended) onPausedRef.current?.(); }],
      // Доигравшая больше не звучит: следующая часть не должна ставить её на паузу.
      ['ended', () => { if (playingNow === el) playingNow = null; dispatch({ type: 'ended' }); onEndedRef.current?.(); }],
      ['error', () => {
        const code = el.error?.code;
        dispatch({ type: 'error', code });
        // ⚠️ Код 4 браузер отдаёт и на 404 — уточняем отдельным запросом,
        // есть ли файл, чтобы не писать «браузер не может» про пропавшую запись
        // (правило — errorKind в lib/audioPlayerState.ts).
        if (code !== 4) return;
        checkFile(el.currentSrc || src, (reachable) => {
          if (alive) dispatch({ type: 'error', code, reachable });
        });
      }],
      ['progress', () => { life(); readLoaded(); }],
      ['loadedmetadata', () => {
        life();
        el.playbackRate = rateRef.current;
        if (Number.isFinite(el.duration)) {
          setDuration(el.duration);
          applyStart();
          return;
        }
        if (knownSeconds != null) {
          applyStart();
          return;
        }
        fixing.current = true;
        try {
          el.currentTime = 1e101;
        } catch {
          fixing.current = false;
        }
      }],
      ['durationchange', () => {
        if (finishFix()) return;
        if (!fixing.current && Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
      }],
      ['timeupdate', () => {
        life();
        if (fixing.current) {
          finishFix();
          return;
        }
        setCurrent(el.currentTime);
        readLoaded();
        onTimeRef.current?.(el.currentTime);
      }],
    ];
    for (const [type, fn] of handlers) el.addEventListener(type, fn);
    if (el.readyState >= 1 && Number.isFinite(el.duration)) applyStart();
    return () => {
      alive = false;
      srcToken.current += 1;
      for (const [type, fn] of handlers) el.removeEventListener(type, fn);
      if (playingNow === el) playingNow = null;
      el.pause();
    };
  }, [src, knownSeconds]);

  // Сторож: нажали «играть», а признаков жизни нет — ошибка с «Повторить».
  useEffect(() => {
    if (state.status !== 'loading' || !state.wantPlay) return;
    // ⚠️ Скрытую вкладку не судим. Chrome не начинает загрузку звука в фоновой
    // вкладке вовсе, пока её не откроют: на стенде 10.09.2026 запрос за файлом
    // так и не ушёл. «Не загрузилась» там была бы ложью — ждём, пока вкладку
    // покажут, и только потом считаем время.
    let id: ReturnType<typeof setTimeout>;
    const fire = () => {
      if (document.hidden) {
        id = setTimeout(fire, STALL_MS);
        return;
      }
      dispatch({ type: 'stalled-timeout' });
    };
    id = setTimeout(fire, STALL_MS);
    return () => clearTimeout(id);
  }, [state.status, state.wantPlay, signal]);

  // Ранний выход: звука нет три секунды — предлагаем повторить загрузку, не
  // объявляя ошибки. Скрытую вкладку не судим по той же причине, что и сторож
  // ниже: Chrome в фоне загрузку не начинает вовсе.
  useEffect(() => {
    if (state.status !== 'loading' || !state.wantPlay || state.slow) return;
    let id: ReturnType<typeof setTimeout>;
    const fire = () => {
      if (document.hidden) {
        id = setTimeout(fire, SOFT_RETRY_MS);
        return;
      }
      dispatch({ type: 'slow-timeout' });
    };
    id = setTimeout(fire, SOFT_RETRY_MS);
    return () => clearTimeout(id);
  }, [state.status, state.wantPlay, state.slow, signal]);

  const startPlaying = (el: HTMLAudioElement) => {
    const token = srcToken.current;
    const p = el.play() as Promise<void> | undefined;
    if (!p) return;
    p.catch((e: unknown) => {
      const name = (e as { name?: string })?.name ?? '';
      dispatch({ type: 'play-rejected', name });
      // «Нет подходящего источника» — то же, что код 4: проверяем, есть ли файл.
      if (name !== 'NotSupportedError') return;
      checkFile(el.currentSrc || src, (reachable) => {
        if (srcToken.current === token) dispatch({ type: 'error', code: 4, reachable });
      });
    });
  };

  // Переход к месту из разбора: перемотка (запись с preload="none" ещё не знает
  // длительности — тогда по loadedmetadata) и запуск обычным путём.
  useEffect(() => {
    const el = ref.current;
    if (!jumpTo || !el) return;
    const go = () => {
      seeked.current = true;
      el.currentTime = jumpTo.sec;
      setCurrent(jumpTo.sec);
    };
    if (el.readyState >= 1) go();
    else el.addEventListener('loadedmetadata', go, { once: true });
    dispatch({ type: 'play-pressed' });
    startPlaying(el);
    return () => el.removeEventListener('loadedmetadata', go);
    // Только по новому нажатию: src и startPlaying меняются вместе с записью.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo?.nonce]);

  // Запуск снаружи (следующая часть мока): только по новому значению. Дослушанную на экзамене
  // не включаем — play() у доигравшего элемента начал бы её с начала.
  useEffect(() => {
    const el = ref.current;
    if (!startNonce || !el || (examOnce && (el.ended || closed))) return;
    dispatch({ type: 'play-pressed' });
    startPlaying(el);
    // Только по новому значению: src и startPlaying меняются вместе с записью.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startNonce]);

  // Дослушанная — в этом показе плеера или раньше в попытке (closed, после F5).
  const examDone = examOnce && (closed || state.status === 'ended');

  const toggle = () => {
    const el = ref.current;
    if (!el || examDone) return;
    if (state.status === 'playing' || (state.status === 'loading' && state.wantPlay)) {
      el.pause();
      dispatch({ type: 'pause-pressed' });
      return;
    }
    dispatch({ type: 'play-pressed' });
    startPlaying(el);
  };

  const retry = () => {
    const el = ref.current;
    if (!el) return;
    dispatch({ type: 'retry' });
    // Загрузка пойдёт с начала — процент снова можно считать честно.
    seeked.current = false;
    setLoaded(null);
    // load() перечитывает ссылку заново — без него браузер помнит отказ.
    el.load();
    startPlaying(el);
  };

  const seek = (value: number) => {
    const el = ref.current;
    if (!el || !Number.isFinite(value)) return;
    el.currentTime = value;
    setCurrent(value);
    seeked.current = true;
    setLoaded(null);
  };

  const changeSpeed = () => {
    const r = nextSpeed(rate);
    setRate(r);
    rateRef.current = r;
    const el = ref.current;
    if (el) {
      el.playbackRate = r;
      el.defaultPlaybackRate = r;
    }
  };

  const busy = state.status === 'loading' && state.wantPlay;
  const playing = state.status === 'playing';
  const total = duration != null && Number.isFinite(duration) && duration > 0 ? duration : null;
  // Подпись кнопки в моке (по-английски, как на экзамене); после конца — только галочка.
  const examLabel = examOnce && !examDone ? t(playing || busy ? 'audioPlayer.examPause' : 'audioPlayer.examPlay') : null;

  useEffect(() => {
    if (total != null) onDuration?.(total);
  }, [total, onDuration]);

  return (
    // data-no-dirty: «играть» внутри задания — не начало ответа (окно «Выйти из урока?», lib/lessonAnswerGuard.ts).
    <div data-no-dirty="" className={cn('ob-audio rounded-card bg-surface-container-low px-1.5 py-1', className)}>
      <audio ref={ref} src={src} preload={preload} className="hidden" />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggle}
          disabled={examDone}
          aria-label={examLabel ?? (busy ? t('audioPlayer.loading') : playing ? t('audioPlayer.pause') : t('audioPlayer.play'))}
          className={cn(
            'flex-none rounded-full bg-primary text-on-primary transition hover:opacity-90 disabled:cursor-default disabled:opacity-40',
            // Мок: подписанная кнопка постоянной ширины — «Pause» и «Play» не двигают шкалу.
            examLabel ? 'inline-flex h-11 min-w-[6.5rem] items-center justify-center gap-1 pl-2.5 pr-3.5 text-sm font-semibold' : 'grid h-11 w-11 place-items-center',
          )}
        >
          <span className={cn('material-symbols-outlined text-[24px]', busy && 'animate-spin')} aria-hidden="true">
            {busy ? 'progress_activity' : examDone ? 'check' : playing ? 'pause' : 'play_arrow'}
          </span>
          {examLabel && <span aria-hidden="true">{examLabel}</span>}
        </button>
        <span className="w-10 flex-none text-right text-xs tabular-nums text-on-surface-variant">{clock(current)}</span>
        <input
          type="range"
          min={0}
          max={total ?? 0}
          step={0.1}
          value={Math.min(current, total ?? 0)}
          // На экзамене шкала только показывает ход записи — перемотки нет.
          disabled={total == null || examOnce}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label={t('audioPlayer.seek')}
          aria-valuetext={clock(current)}
          className="h-11 min-w-0 flex-1 cursor-pointer accent-primary disabled:cursor-default"
        />
        <span className="w-10 flex-none text-xs tabular-nums text-on-surface-variant">{total != null ? clock(total) : '–:––'}</span>
        {!examOnce && (
          <button
            type="button"
            onClick={changeSpeed}
            aria-label={t('audioPlayer.speed')}
            className="h-11 min-w-11 flex-none rounded-button px-1.5 text-xs font-semibold tabular-nums text-on-surface transition hover:bg-surface-container-high"
          >
            {rate}×
          </button>
        )}
      </div>
      {busy && (loaded != null || state.slow) && (
        <div className="px-1.5 pb-0.5">
          {loaded != null && (
            <div className="flex items-center gap-2">
              {/* ⚠️ Полоса — ОТДЕЛЬНЫЙ элемент, а не фон у ползунка: правило темы
                  бьёт классы на элементе сильнее (техдолг п. 88). */}
              <div className="h-0.5 flex-1 overflow-hidden rounded-full bg-surface-container-high">
                <div className="h-full bg-primary" style={{ width: `${loaded}%` }} />
              </div>
              <span className="flex-none text-[11px] tabular-nums text-on-surface-variant">
                {t('audioPlayer.loadedPercent', { percent: loaded })}
              </span>
            </div>
          )}
          {state.slow && (
            <div className="flex flex-wrap items-center gap-x-3">
              <span role="status" className="mr-auto text-xs text-on-surface-variant">
                {t('audioPlayer.stillQuiet')}
              </span>
              <button
                type="button"
                onClick={retry}
                className="flex min-h-11 items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                <span className="material-symbols-outlined text-sm" aria-hidden="true">refresh</span>
                {t('audioPlayer.retryLoad')}
              </button>
            </div>
          )}
        </div>
      )}
      {(state.status === 'error' || downloadUrl) && (
        <div className="flex flex-wrap items-center justify-end gap-x-3 px-1.5">
          {state.status === 'error' && (
            <>
              <span role="alert" className="mr-auto text-xs text-error">
                {state.error === 'unsupported' ? t('audioPlayer.unsupported') : t('audioPlayer.error')}
              </span>
              <button
                type="button"
                onClick={retry}
                className="flex min-h-11 items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                <span className="material-symbols-outlined text-sm" aria-hidden="true">refresh</span>
                {t('audioPlayer.retry')}
              </button>
            </>
          )}
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              download
              className="flex min-h-11 items-center text-[11px] text-on-surface-variant underline-offset-2 hover:underline"
            >
              {t('audioPlayer.download')}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
